<template>
    <nav role="navigation">
        <!-- Desktop sidebar -->
        <aside class="fixed left-2 top-2 z-30 hidden w-64 flex-col gap-2 lg:flex"
            :class="hasPlayer ? 'bottom-24' : 'bottom-2'" aria-label="Main">
            <div class="rounded-xl bg-nomad-850 px-3 pb-3 pt-5">
                <Link href="/" class="mb-4 block px-3" aria-label="Heesify home">
                    <img src="@/assets/logo-full.svg" alt="Heesify" class="-ml-1 h-11 select-none" />
                </Link>
                <ul class="grid gap-0.5">
                    <li v-for="menu in primaryMenus" :key="menu.permlink">
                        <Link :href="'/' + menu.permlink"
                            class="flex items-center gap-4 rounded-lg px-3 py-2.5 text-[0.9375rem] font-medium transition-colors"
                            :class="isActive(menu) ? 'text-white' : 'text-nomad-300 hover:text-white'">
                            <span class="w-5 text-center text-lg" :class="[menu.icon, isActive(menu) ? 'text-nomad-accent' : '']" aria-hidden="true"></span>
                            {{ menu.title }}
                        </Link>
                    </li>
                    <li v-for="link in newTabLinks" :key="link.href">
                        <a :href="link.href" target="_blank" rel="noopener"
                            class="flex items-center gap-4 rounded-lg px-3 py-2.5 text-[0.9375rem] font-medium text-nomad-300 transition-colors hover:text-white">
                            <img v-if="link.image" :src="link.image" alt="" class="h-5 w-5 object-contain" />
                            <span v-else class="w-5 text-center text-lg" :class="link.icon" aria-hidden="true"></span>
                            <span class="flex-1">{{ link.title }}</span>
                        </a>
                    </li>
                </ul>
            </div>

            <div class="flex min-h-0 flex-1 flex-col rounded-xl bg-nomad-850">
                <div class="flex items-center justify-between px-6 pb-2 pt-5">
                    <h2 class="text-[0.9375rem] font-medium text-nomad-200">Recently played</h2>
                    <button v-if="playedSongs.length" type="button" @click="clearHistory"
                        class="-mr-2 rounded-full px-2 py-1 text-xs font-medium text-nomad-400 transition-colors hover:text-white"
                        aria-label="Clear recently played">
                        Clear
                    </button>
                </div>
                <div class="nl-scroll min-h-0 flex-1 overflow-y-auto px-3 pb-3">
                    <ul v-if="playedSongs.length" class="grid gap-0.5">
                        <li v-for="song in playedSongs" :key="song.id + '-' + song.playedAt">
                            <button type="button" @click="playFromHistory(song)"
                                class="flex w-full items-center gap-3 rounded-lg p-2 text-left transition-colors hover:bg-white/[0.05]">
                                <img :src="song.cover || song.artist?.image" alt="" class="h-10 w-10 shrink-0 rounded object-cover" loading="lazy" />
                                <span class="min-w-0 flex-1">
                                    <span class="block truncate text-sm"
                                        :class="currentSong.id === song.id ? 'text-nomad-accent' : 'text-white'">{{ song.name }}</span>
                                    <span class="block truncate text-xs text-nomad-400">{{ song.artist?.name }}</span>
                                </span>
                                <span v-if="currentSong.id === song.id && isPlaying" class="nl-eq mr-1 shrink-0"><span></span><span></span><span></span></span>
                            </button>
                        </li>
                    </ul>
                    <p v-else class="px-3 py-2 text-sm leading-relaxed text-nomad-400">
                        Songs you play will show up here.
                    </p>
                </div>
                <div class="border-t border-white/[0.06] px-6 py-4 text-xs text-nomad-400">
                    <a href="/pages/apps" class="hover:text-white">Get the app</a>
                    <span class="mx-1.5">·</span>
                    <a href="/pages/privacy-policy" class="hover:text-white">Privacy</a>
                    <span class="mx-1.5">·</span>
                    <a href="/pages/terms" class="hover:text-white">Terms</a>
                </div>
            </div>
        </aside>

        <!-- Mobile tab bar -->
        <div class="fixed inset-x-0 bottom-0 z-40 border-t border-white/[0.06] bg-black/95 backdrop-blur lg:hidden" data-tab-bar>
            <ul class="mx-auto grid h-16 max-w-md grid-cols-5">
                <li v-for="tab in tabs" :key="tab.title">
                    <Link v-if="tab.permlink !== null" :href="'/' + tab.permlink"
                        class="flex h-full flex-col items-center justify-center gap-1 text-[0.6875rem]"
                        :class="isActive(tab) ? 'text-white' : 'text-nomad-400'">
                        <span class="text-xl" :class="tab.icon" aria-hidden="true"></span>
                        {{ tab.title }}
                    </Link>
                    <button v-else type="button" @click="toggleMenu"
                        class="flex h-full w-full flex-col items-center justify-center gap-1 text-[0.6875rem] text-nomad-400"
                        aria-controls="site-navigation-menu" :aria-expanded="isOpen">
                        <span class="text-xl" :class="tab.icon" aria-hidden="true"></span>
                        {{ tab.title }}
                    </button>
                </li>
            </ul>
        </div>

        <!-- Menu drawer -->
        <div
            id="site-navigation-menu"
            class="fixed inset-0 z-[70] flex bg-black/60 transition-opacity duration-300"
            :class="isOpen ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'"
            @click.self="toggleMenu"
        >
            <aside
                class="nl-scroll flex h-full w-[85vw] max-w-sm shrink-0 flex-col overflow-y-auto bg-nomad-850 transition-transform duration-300 ease-out"
                :class="isOpen ? 'translate-x-0' : '-translate-x-full'"
                :inert="!isOpen"
                aria-label="Main menu"
            >
                <div class="flex items-center justify-between px-6 py-5">
                    <Link href="/" aria-label="Heesify home" @click="toggleMenu">
                        <img src="@/assets/logo-full.svg" alt="Heesify" class="-ml-1 h-12 select-none" />
                    </Link>
                    <button
                        type="button"
                        @click="toggleMenu"
                        class="flex h-10 w-10 items-center justify-center rounded-full text-xl text-nomad-300 transition-colors hover:text-white"
                        aria-label="Close menu"
                    >
                        <span class="icon-x" aria-hidden="true"></span>
                    </button>
                </div>

                <div v-if="auth && auth.id" class="mx-6 mb-4 flex items-center gap-3">
                    <img :src="auth.avatar" alt="Profile picture" class="h-11 w-11 rounded-full object-cover" />
                    <div class="min-w-0">
                        <h3 class="truncate text-sm font-semibold text-white">{{ auth.name }}</h3>
                        <span class="text-xs text-nomad-400">Member since {{ memberSinceYear(auth.created_at) }}</span>
                    </div>
                </div>

                <ul class="grid gap-0.5 px-3 pb-6">
                    <li v-for="menu in visibleMenus" :key="menu.permlink">
                        <Link
                            :href="'/' + menu.permlink"
                            class="flex items-center gap-4 rounded-lg px-3 py-3 text-[0.9375rem] font-medium transition-colors"
                            :class="isActive(menu) ? 'text-white' : 'text-nomad-300 hover:text-white'"
                            @click="toggleMenu"
                        >
                            <span class="w-5 text-center text-lg" :class="[menu.icon, isActive(menu) ? 'text-nomad-accent' : '']" aria-hidden="true"></span>
                            <span class="flex-1">{{ menu.title }}</span>
                        </Link>
                    </li>
                    <li v-for="link in newTabLinks" :key="link.href">
                        <a :href="link.href" target="_blank" rel="noopener"
                            class="flex items-center gap-4 rounded-lg px-3 py-3 text-[0.9375rem] font-medium text-nomad-300 transition-colors hover:text-white">
                            <img v-if="link.image" :src="link.image" alt="" class="h-5 w-5 object-contain" />
                            <span v-else class="w-5 text-center text-lg" :class="link.icon" aria-hidden="true"></span>
                            <span class="flex-1">{{ link.title }}</span>
                        </a>
                    </li>
                </ul>

                <div class="mt-auto grid gap-2 border-t border-white/[0.06] p-5">
                    <a
                        v-if="isLoggedIn"
                        :href="'https://pay.rev.cat/qctnwhyrublbpkwd/' + auth.firebase_uid"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="nl-btn-primary"
                    >
                        Buy subscription
                    </a>
                    <!-- Buttons, not links: AdSense vignettes take over link clicks -->
                    <button v-if="!isLoggedIn" type="button" @click="login" class="nl-btn-primary">
                        Log in
                    </button>
                    <button v-else type="button" @click="logoutUser" class="nl-btn-ghost">
                        Log out
                    </button>
                    <Link
                        v-if="auth && auth.id && auth.isAdmin"
                        href="/backstage"
                        class="nl-btn-ghost"
                        @click="toggleMenu"
                    >
                        Backstage
                    </Link>
                </div>
            </aside>
        </div>
    </nav>
</template>
<script>
import { Link } from "@inertiajs/vue3";
import { mapState, mapActions } from "vuex";
import luuqaIcon from "@/assets/luuqa-icon.svg";
export default {
    data() {
        return {
            isOpen: false,
            // These are their own sites within the site, so they open in a new tab
            newTabLinks: [
                { href: "/luuqa", title: "Luuqa", image: luuqaIcon },
                { href: "/sponsor", title: "Sponsored ads", icon: "icon-volume-2" },
            ],
            menus: [
                { permlink: "", title: "Home", icon: "icon-home" },
                { permlink: "just-added", title: "Just added", icon: "icon-music" },
                { permlink: "playlists", title: "Playlists", icon: "icon-list" },
                { permlink: "podcasts", title: "Podcasts", icon: "icon-mic" },
                { permlink: "smart", title: "Smart Music", icon: "icon-sliders" },
                { permlink: "favorites", title: "Likes", icon: "icon-heart" },
            ],
            tabs: [
                { permlink: "", title: "Home", icon: "icon-home" },
                { permlink: "search", title: "Search", icon: "icon-search" },
                { permlink: "playlists", title: "Playlists", icon: "icon-list" },
                { permlink: "podcasts", title: "Podcasts", icon: "icon-mic" },
                { permlink: null, title: "More", icon: "icon-menu" },
            ],
        };
    },
    computed: {
        ...mapState({
            auth: (state) => state.user.auth,
            isLoggedIn: (state) => state.user.isLoggedIn,
        }),
        currentSong() {
            return this.$store.getters["current/currentSong"] || {};
        },
        isPlaying() {
            return this.$store.getters["current/isPlaying"];
        },
        hasPlayer() {
            return !!(this.currentSong && this.currentSong.id);
        },
        playedSongs() {
            return this.$store.getters["current/playedSongs"] || [];
        },
        primaryMenus() {
            return this.visibleMenus;
        },
        visibleMenus() {
            return this.menus.filter((menu) => menu.permlink !== "favorites" || this.isLoggedIn);
        },
    },
    methods: {
        ...mapActions("user", ["login", "logoutUser"]),
        clearHistory() {
            this.$store.dispatch("current/clearPlayedSongs");
        },
        playFromHistory(song) {
            this.$store.dispatch("current/prepareCurrent", {
                id: song.id,
                type: song.content_type || "SONG",
                keepOrder: true,
            });
        },
        isActive(menu) {
            const url = this.$page.url.split("?")[0];
            if (menu.permlink === "") return url === "/";
            return url.startsWith("/" + menu.permlink);
        },
        toggleMenu() {
            this.isOpen = !this.isOpen;
        },
        memberSinceYear(createdAt) {
            if (!createdAt) return "";

            const date = new Date(createdAt);
            return Number.isNaN(date.getTime()) ? "" : date.getFullYear();
        },
    },
    components: { Link },
};
</script>
